import React, { useState } from "react";
import { PDFDocument } from "pdf-lib";

import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
	useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AppButton from "./AppButton";
import { MdDelete } from "react-icons/md";
import TitleHeading from "./TitleHeading";
import { logOperationSuccess } from "../lib/analytics";

const SortableItem = ({ id, file, previewUrl, handleDeleteAction }) => {
	const { attributes, listeners, setNodeRef, transform, transition } =
		useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		<div
			ref={setNodeRef}
			{...attributes}
			// {...listeners}
			style={style}
			className="bg-gray-700  text-white rounded flex items-center gap-2 font-semibold cursor-grab hover:bg-orange-950"
		>
			{/* <img
				src={previewUrl}
				alt="Preview"
				style={{ width: 60, height: 80, marginRight: 10 }}
			/> */}
			<span
				onClick={(e) => {
					e.stopPropagation(); // Prevent triggering drag events

					handleDeleteAction(file.name);
				}}
				className="absolute top-3 right-1 cursor-pointer z-50"
			>
				<MdDelete color="tomato" size={20} />
			</span>

			<span {...listeners} className="w-[100%] px-2 py-3">
				{file.name}
			</span>
		</div>
	);
};

const PDFMergeTool = () => {
	const [files, setFiles] = useState([]);
	const [isDragging, setIsDragging] = useState(false);
	// const [previews, setPreviews] = useState({});

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const addFiles = async (newFiles) => {
		const filtered = Array.from(newFiles).filter(
			(file) =>
				file.type === "application/pdf" &&
				!files.some((f) => f.name === file.name)
		);
		if (filtered.length > 0) {
			setFiles((prev) => [...prev, ...filtered]);

			// for (const file of filtered) {
			// 	const preview = await generatePreview(file);
			// 	setPreviews((prev) => ({ ...prev, [file.name]: preview }));
			// }
		}
	};

	const handleFileInputChange = (e) => {
		addFiles(e.target.files);
	};

	// const handleFileChange = (e) => {
	// 	const fileList = Array.from(e.target.files);
	// 	const uniqueFiles = fileList.filter(
	// 		(f) => !files.find((x) => x.name === f.name)
	// 	);
	// 	setFiles((prev) => [...prev, ...uniqueFiles]);
	// };

	const handleDragOver = (e) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = () => {
		setIsDragging(false);
	};

	const handleDrop = (e) => {
		e.preventDefault();
		setIsDragging(false);
		if (e.dataTransfer.files.length > 0) {
			addFiles(e.dataTransfer.files);
		}
	};

	const handleDragEnd = (event) => {
		const { active, over } = event;
		if (active.id !== over.id) {
			const oldIndex = files.findIndex((file) => file.name === active.id);
			const newIndex = files.findIndex((file) => file.name === over.id);
			setFiles((items) => arrayMove(items, oldIndex, newIndex));
		}
	};

	const mergePDFs = async () => {
		const start = Date.now();

		try {
			const mergedPdf = await PDFDocument.create();

			for (const file of files) {
				const arrayBuffer = await file.arrayBuffer();
				const pdf = await PDFDocument.load(arrayBuffer);
				const copiedPages = await mergedPdf.copyPages(
					pdf,
					pdf.getPageIndices()
				);
				copiedPages.forEach((page) => mergedPdf.addPage(page));
			}

			const pdfBytes = await mergedPdf.save(); // ✅ success point
			const blob = new Blob([pdfBytes], { type: "application/pdf" });
			const url = URL.createObjectURL(blob);

			// open/download result
			window.open(url);

			// analytics payload
			const totalBytes = files.reduce((sum, f) => sum + (f?.size || 0), 0);

			// ✅ log operation success (don’t block UI)
			logOperationSuccess({
				operation: "MERGE",
				page: "/pdf-merge", // set your actual route/path if different
				fileCount: files.length,
				totalBytes,
				outputType: "pdf",
				durationMs: Date.now() - start,
			}).catch(console.error);
		} catch (err) {
			console.error("Merge failed:", err);
			// optional: show toast/error UI
		}
	};

	// Delete a box and rearrange the grid
	const handleDelete = (fileName) => {
		setFiles((prev) => {
			// Remove the selected box and reindex the remaining boxes
			const updatedFiles = prev.filter((f) => f.name !== fileName);
			return updatedFiles;
		});
	};

	return (
		<div className="px-4">
			<TitleHeading title="Combine PDF Documents" />

			{/* Drag-and-Drop Zone */}
			<div
				onClick={() => document.getElementById("fileInputSelector").click()}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				className={`${
					isDragging ? "bg-zinc-600" : "bg-zinc-800"
				} border-2 border-dashed border-gray-600 rounded-lg h-[150px] flex items-center justify-center cursor-pointer mb-8 bg-opacity-30`}
			>
				{isDragging ? (
					"Drop PDF files here..."
				) : (
					<p className="text-center font-semibold">
						Drag and drop PDF files here <br />
						Or Click to Select files
					</p>
				)}
			</div>

			{/* Button Sections */}
			<div className="flex flex-col gap-2 sm:flex-row justify-between items-center">
				<div>
					<input
						type="file"
						accept="application/pdf"
						multiple
						onChange={handleFileInputChange}
						id="fileInputSelector"
						className="w-full sm:w-fit bg-slate-900 text-white px-2 py-3 rounded cursor-pointer hidden"
					/>
				</div>

				{files.length > 0 && (
					<div className="w-full sm:w-fit flex justify-between gap-3">
						<AppButton
							title={"Clear List"}
							onClick={() => setFiles([])}
							bgColor={"bg-orange-900"}
						/>
						<AppButton title={"Merge PDFs"} onClick={mergePDFs} />
					</div>
				)}
			</div>

			{/* Reorder List */}
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={handleDragEnd}
			>
				<SortableContext
					items={files.map((f) => f.name)}
					strategy={verticalListSortingStrategy}
				>
					<div
						style={{ marginTop: "1rem" }}
						className="bg-slate-950 text-white p-5 flex flex-col gap-2 rounded min-h-[300px]"
					>
						{files.length === 0 ? (
							<h1 className="text-zinc-400 italic font-semibold text-lg">
								Select PDF files to Merge.
							</h1>
						) : (
							files.map((file) => (
								<div className="relative" key={file.name}>
									<SortableItem
										key={file.name}
										id={file.name}
										file={file}
										handleDeleteAction={(fileName) => handleDelete(fileName)}
										// previewUrl={previews[file.name]}
									/>
								</div>
							))
						)}
					</div>
				</SortableContext>
			</DndContext>
		</div>
	);
};

export default PDFMergeTool;
